import React from 'react';
import { buildOrderListDoc } from '../utils/orderListPdf';
import ListPrintMenuItems from './ListPrintMenuItems';

/**
 * The `Print all` row of an order page's header-band `Actions ▾` menu.
 *
 * Product owner, 2026-08-10, deciding the open question: *"print all is a
 * printable list, likey landscape to fit all columns on."*
 *
 * 🔑 **ONE ROW PER ORDER, EVERY FILTERED ROW, PAGINATION IGNORED — and the
 * document SAYS SO in its own subtitle.** That sentence is not decoration: a
 * sheet of paper carries no page-size selector, so a printed list that silently
 * held 25 of 258 orders would be indistinguishable from a complete one. The
 * scope line is built by `utils/orderListPdf.js`; this component's only job is
 * to hand it the right array.
 *
 * 🚨 **IT IS `filteredOrders`, NOT `paginatedOrders`, AND THAT IS THE WHOLE
 * MEANING OF "ALL".** Passing the page slice would produce a document that is
 * correct, plausible and wrong — the exact failure the subtitle exists to make
 * impossible. The caller supplies the array; there is deliberately no default.
 *
 * 🪤 **DO NOT ADD `orientation: 'landscape'` TO THE `useSharePdf` CALL.** That
 * option feeds ONLY the html2pdf path (`useSharePdf.js:189,210`) and is inert on
 * the pdfmake path taken here — `OrderPdfMenuItems.js:62` passes
 * `orientation: 'portrait'` today and it does nothing at all. Landscape comes
 * from `pageOrientation` inside the doc definition, and passing it here as well
 * would look like it worked while changing nothing, which is worse than omitting
 * it.
 *
 * ✅ **`Download list (PDF)` NOW EXISTS, BEHIND THE OPTIONAL `downloadTestId`
 * PROP — AND THE ENTRY BELOW IS KEPT BECAUSE THE *SEQUENCE* IS THE LESSON, NOT
 * THE OUTCOME.** It read: *"THERE IS NO `Download list (PDF)` ROW, AND ITS
 * ABSENCE IS A DECISION. `useSharePdf` returns `handleDownload` from the same
 * generated blob, so it would cost one more button — but the owner asked for
 * **Print all** and an unrequested extra menu item is scope creep on a bar he
 * has twice complained is crowded. It is one line away if he asks."*
 * **He asked**, 2026-08-10: *"to approve page actions menu should have export
 * all excell as we already have print all maybe even export pdf list. trying to
 * stay consistant"*. So the decision is SUPERSEDED, not overturned — declining
 * it as scope creep was right at the time, and the note is left standing so the
 * next reader finds an owner ruling rather than an argument for removing it
 * again.
 *
 * 🔑 **ONE `useSharePdf` INSTANCE FEEDS BOTH ROWS.** Print and Download come off
 * the same hook and the same `buildOrderListDoc`, so the two documents cannot
 * diverge and a click on either pays for exactly one generation. A second
 * component would have meant a second hook, a second copy of six props, and two
 * things to keep in step. `pendingAction` disables BOTH while either is working.
 *
 * 🚨 **NO PERMISSION GATE, MATCHING `OrderPdfMenuItems`.** The document is built
 * CLIENT-SIDE from rows the caller is already being shown, so it discloses
 * nothing new — the same argument that leaves the per-row PDF rows ungated. It
 * is emphatically NOT the CSV export on Dept Orders / To Approve, which goes to a server endpoint and is
 * gated there by `canViewOrder`.
 *
 * Props:
 *  - orders: the FILTERED list, every row (see above). Required.
 *  - headerTitle / departmentName / filterSummary / totalCount: passed through.
 *  - onDone: called after the action completes, so the panel can close.
 *  - testId: e.g. `dept-actions-print-all`. 🚨 A test contract.
 *  - downloadTestId: OPTIONAL. Supply it and a second row, `Download list
 *    (PDF)`, renders BELOW `Print all` off the same hook. **Omit it and this
 *    component is byte-identical to what shipped** — the row does not exist, not
 *    a hidden one and not an empty flex child. Both order pages pass it; there
 *    is no third caller.
 */
export default function OrderListPrintMenuItem({
  orders,
  headerTitle,
  departmentName = '',
  filterSummary = '',
  totalCount = null,
  filename = 'orders.pdf',
  onDone,
  testId,
  downloadTestId,
}) {
  // 🔀 2026-09-27: the two rows and their one `useSharePdf` moved VERBATIM to
  // `./ListPrintMenuItems.js` (the hires pages print hire lines through it);
  // this component is the ORDER document handed to it, and renders what it did.
  return (
    <ListPrintMenuItems
      renderPdfMakeDoc={() => buildOrderListDoc({
        orders: orders || [],
        headerTitle,
        departmentName,
        filterSummary,
        totalCount,
      })}
      filename={filename}
      title={headerTitle}
      rowNoun="order"
      onDone={onDone}
      testId={testId}
      downloadTestId={downloadTestId}
    />
  );
}
